import Head from "next/head";
import { useRouter } from "next/router";
import useSWR from "swr";

import { useMemo } from "react";

import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import UnverifiedPhoneNumber from "components/DiscussionRestrictionState/UnverifiedPhone";
import { entityUrl } from "components/EntityPageLayout";
import SignUpForm from "components/SignUpForm";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { baseFetcher } from "utils/baseFetcher";
import shortSecurity from "utils/shortSecurity";

import EntityPageDiscussionsList from "./EntityPageDiscussionsList";

type EntityPageDiscussionsProps = {};

const EntityPageDiscussions = ({}: EntityPageDiscussionsProps) => {
  const router = useRouter();
  const slug = router.query.slug as string;

  const { authenticated, account } = useAuthentication();

  const { data: phoneNumber, isValidating: isPhoneNumberValidating } = useSWR(
    account?.id ? `/api/v2/accounts/${account?.id}/phone-number` : null,
    baseFetcher
  );

  const { data: entity } = useApi(entityUrl(slug));
  const { prettyName: name, security } = entity || {};
  const shortedSecurity = shortSecurity(security);
  const pageTitleName = useMemo(() => {
    if (shortedSecurity) {
      return `${name} | ${shortedSecurity} `;
    }
    return name;
  }, [shortedSecurity, name]);
  const title = `${pageTitleName} - Discussions`;
  const description = "Sign up to view latest discussions on Smartkarma";

  const content = useMemo(() => {
    if (
      account?.isPreviewPass &&
      !isPhoneNumberValidating &&
      !(phoneNumber?.verified || phoneNumber?.skipVerification)
    ) {
      return <UnverifiedPhoneNumber />;
    }

    return !account || account?.canViewComment ? (
      <EntityPageDiscussionsList />
    ) : account?.company?.disableViewingComments ? (
      <i>Your Compliance Team has restricted your access to this feature.</i>
    ) : (
      <i>
        As a {account?.accountType} holder, you do not have access to
        discussions
      </i>
    );
  }, [
    account,
    isPhoneNumberValidating,
    phoneNumber?.skipVerification,
    phoneNumber?.verified,
  ]);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      {authenticated ? (
        <CustomCard
          className={twMerge(
            "mx-0 mt-0 rounded-t-none",
            "md:rounded-xl md:mt-[45px]",
            "mb-0 md:mb-[30px]",
            "p-[15px] md:p-[30px]",
            "min-h-screen"
          )}
        >
          {content}
        </CustomCard>
      ) : (
        <div className="md:mt-[45px] md:shadow">
          <SignUpForm entityId={entity?.id} type="insight-signup" />
        </div>
      )}
    </>
  );
};

export default EntityPageDiscussions;
